import { useEffect, useState } from 'react';
import { WithSingleSelect } from '@components/design';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';

import { FeatureOption, FilterKeys, Filters, getPageTypeWithPageName } from '@utils/pageConfig/Types';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { ColumnConfig, SortDirectionType, SortFieldDisplayProps } from '@components/design/migrated/Table';
import { useSelector } from 'react-redux';
import {
  ColumnHeaderForPA,
  getDisplayNameFromHeaderKey,
  getSortDirectionLabel,
  SortKeys,
  SortLabels,
  stackedChartSortByOptions,
} from '@models/PerformanceAnalysisboard';
import { PERFORMANCE_ANALYSIS_CARD_TITLE } from '@pages/Utilities';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { NO_OPTIONS } from '@utils/StringConstants';
import FeatureSelector from '../feature-selector/FeatureSelector';
import { FeatureSelectorType } from '../feature-selector/Types';
import { formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { isAllowSearchBox } from '@utils/FilterUtils';

interface SegmentByChartPickersProps {
  isLoading: boolean;
  label: string;
  kpisConfig: ColumnConfig[];
  sorting: SortingOptions;
  stackedChartSorting: SortingOptions;
  updateSorting: (options: SortingOptions) => void;
  updateStackChartSorting: (options: SortingOptions) => void;
  featureFilters: Filters[];
  segmentByFeature: FeatureOption;
  setSegmentByFeature: (f: FeatureOption) => void;
  showPerformanceWithin: FeatureOption;
  featureSelectorType: FeatureSelectorType;
  featureHandler: () => void;
  onKpiSelectHandler?: () => void;
  segmentByHandler: (feat: FeatureOption) => void;
  currencyLabel: string;
  volume: string;
  sortByStackedChart: SortFieldDisplayProps;
  setSortByStackedChart: (sortByStackedChart: SortFieldDisplayProps) => void;
}

export const SegmentByChartPickers = ({
  isLoading = false,
  label,
  kpisConfig,
  sorting,
  stackedChartSorting,
  updateSorting,
  updateStackChartSorting,
  featureFilters,
  segmentByFeature,
  setSegmentByFeature,
  showPerformanceWithin,
  featureSelectorType,
  featureHandler,
  onKpiSelectHandler,
  segmentByHandler,
  currencyLabel,
  volume,
  sortByStackedChart,
  setSortByStackedChart,
}: SegmentByChartPickersProps): JSX.Element => {
  const chartName = `${PERFORMANCE_ANALYSIS_CARD_TITLE} ${ElementType.STACKED_CHART}`;
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const kpiOptions = kpisConfig.map((kpi) => ({
    label: `${formatLabelIntoRevenuePriceTitle(getDisplayNameFromHeaderKey(kpi.headerKey, volume), currencyLabel)}`,
    value: kpi.headerKey,
  }));

  const [kpiValue, setKpiValue] = useState<string | null>(sorting.headerKey);
  const [sortValue, setSortValue] = useState<string | null>(
    `${sortByStackedChart.sortKey || SortKeys.Absolute}|${sortByStackedChart.label}|${
      sortByStackedChart.sortDirection
    }`,
  );

  const handleChartInteractionTracking = (optionChosen: string, chartElementName: ChartElementName) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: chartElementName,
      elementType: ChartElementType.FILTER,
      optionChosen,
      chartName,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };

  // Remove showPerformanceWithin option from Segment By filter
  const removeDuplicateSegByFilterOption = (selectedOption: Filters[]) => {
    return selectedOption.filter((x) => x.filterTitle !== showPerformanceWithin.value);
  };
  const noFeature: {
    value: string;
    label: string;
    dataType: string;
    feature: string;
    filterKey: FilterKeys;
  } = {
    value: '',
    label: NO_OPTIONS,
    dataType: '',
    feature: '',
    filterKey: -1,
  };

  const sortOptions = stackedChartSortByOptions.map((sortField) => ({
    label: `${sortField.label} ${getSortDirectionLabel(
      sortField.sortDirection,
      SortKeys[sortField.sortKey as keyof typeof SortKeys],
    )}`,
    value: `${sortField.sortKey}|${sortField.label}|${sortField.sortDirection}`,
  }));

  useEffect(() => {
    const selectedOption = kpiOptions.find((o) => o.value === kpiValue);
    if (selectedOption && selectedOption.value !== sorting.headerKey) {
      updateStackChartSorting({
        ...stackedChartSorting,
        headerKey: selectedOption.value,
      });
      if (
        ![ColumnHeaderForPA.marketRetailerRevenueShare, ColumnHeaderForPA.marketRetailerVolumeShare].includes(
          selectedOption.value as ColumnHeaderForPA,
        )
      ) {
        // When KPI other than Revenue or Volume Share is selected, it should update back pages config sorting options
        // to allow it to be carried over to other view
        updateSorting({
          ...sorting,
          headerKey: selectedOption.value,
        });
      }
      handleChartInteractionTracking(
        `${formatLabelIntoRevenuePriceTitle(getDisplayNameFromHeaderKey(selectedOption.value, volume), currencyLabel)}`,
        ChartElementName.KPI,
      );
      onKpiSelectHandler?.();
    }
  }, [kpiValue]);

  useEffect(() => {
    const selectedOption = sortOptions?.find((o) => o.value === sortValue);
    if (
      selectedOption &&
      selectedOption.value !==
        `${sortByStackedChart.sortKey}|${sortByStackedChart.label}|${sortByStackedChart.sortDirection}`
    ) {
      const [sortKey, sortlabel, sortDir] = selectedOption.value.split('|');

      const newSortByStackedChart = {
        sortKey: SortKeys[sortKey as keyof typeof SortKeys],
        label: SortLabels[sortlabel as keyof typeof SortLabels],
        sortDirection: SortDirectionType[sortDir as keyof typeof SortDirectionType],
      };

      setSortByStackedChart(newSortByStackedChart);
      handleChartInteractionTracking(
        `${sortlabel} ${getSortDirectionLabel(
          SortDirectionType[sortDir as keyof typeof SortDirectionType],
          SortKeys[sortKey as keyof typeof SortKeys],
        )}`,
        ChartElementName.SORT_BY,
      );
    }
  }, [sortValue]);

  const mainFiltersState = useSelector((state: any) => state.mainFilters);
  const defaultValue: FeatureOption = mainFiltersState?.feature.dataType === '' ? noFeature : segmentByFeature;
  return (
    <div>
      <div className="flex-row flex-wrap gap-4 mb-6">
        <div
          style={{ width: '400px' }}
          data-testid={'kpi-selector'}
          key={`${kpiOptions}-${kpiValue}`} //NOSONAR typescript:S6551
        >
          <WithSingleSelect
            id="kpi-feature-selector"
            name="kpi-feature-selector"
            label="KPI"
            disabled={isLoading}
            value={kpiValue}
            options={kpiOptions}
            onChange={(option) => {
              setKpiValue(option);
            }}
            combobox={isAllowSearchBox(kpiOptions)}
          />
        </div>
        <div style={{ width: '400px' }}>
          <FeatureSelector
            isDisabled={featureSelectorType.isDisabled || isLoading}
            label={'Show performance within'}
            featureValues={featureSelectorType.featureFilters}
            featureOptions={(feat: FeatureOption) => featureSelectorType.setFeature(feat)}
            selectedOption={featureSelectorType.feature}
            withoutDiv={true}
            featureOnChange={featureHandler}
            chartName={chartName}
          />
        </div>
        <div style={{ width: '400px' }} data-testid={'segment-selector'}>
          <FeatureSelector
            isDisabled={isLoading}
            label={label}
            featureValues={removeDuplicateSegByFilterOption(featureFilters)}
            featureOptions={(featureOption: FeatureOption) => setSegmentByFeature(featureOption)}
            selectedOption={defaultValue}
            withoutDiv={true}
            id={'segment-by-feature-selector'}
            featureOnChange={segmentByHandler}
            chartName={chartName}
          />
        </div>
        <div
          style={{ width: '400px' }}
          data-testid={'sortBy-kpi-selector'}
          key={`${sortOptions}-${sortValue}`} //NOSONAR typescript:S6551
        >
          <WithSingleSelect
            id="sortby-feature-selector"
            name="sortby-feature-selector"
            label="Sort by"
            options={sortOptions}
            disabled={isLoading}
            value={sortValue}
            onChange={(option) => {
              setSortValue(option);
            }}
            combobox={isAllowSearchBox(sortOptions)}
          />
        </div>
      </div>
    </div>
  );
};
